import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import * as React from 'react';
import { State } from '../../util/interactiveState.js';
import { Box } from '../Box/Box.js';
import { Button as ButtonMemoized } from '../Button/Button.js';
import { hoverState } from '../TextLink/TextLink.js';
import { Typography } from '../Typography/Typography.js';
import styled from 'styled-components';
import FastForwardIndicator from './FastForwardIndicator.js';

var _excluded = ["count", "current", "onChange", "clickableCurrent", "nextLabel", "nextLabelDataElementName", "previousLabel", "previousLabelDataElementName", "fastForwardLabel", "fastBackwardLabel", "navigationSectionLabel", "itemLabel", "activeItemLabel"];
var PREVIOUS_FORWARD_VALUE = -1;
var NEXT_FORWARD_VALUE = -2;
var MAX_TO_SHOW = 5;
var getPageOptions = function getPageOptions(index, pageCount) {
  var left = index - 1;
  var right = index + 1;
  var pageIndices = [index];
  while (pageIndices.length < MAX_TO_SHOW && (left >= 0 || right <= pageCount - 1)) {
    if (left >= 0) {
      pageIndices.unshift(left);
      left -= 1;
    }
    if (right <= pageCount - 1) {
      pageIndices.push(right);
      right += 1;
    }
  }
  if (pageIndices[0] > 0) {
    if (pageIndices[0] > 1) {
      pageIndices.unshift(PREVIOUS_FORWARD_VALUE);
    }
    pageIndices.unshift(0);
  }
  if (pageIndices[pageIndices.length - 1] < pageCount - 1) {
    if (pageIndices[pageIndices.length - 1] < pageCount - 2) {
      pageIndices.push(NEXT_FORWARD_VALUE);
    }
    pageIndices.push(pageCount - 1);
  }
  return pageIndices;
};
var TypographyStyled = /*#__PURE__*/styled(Typography).withConfig({
  displayName: "Pagination__TypographyStyled",
  componentId: "sc-18orsnb-0"
})(_defineProperty({}, "&".concat(State.Hover), hoverState()));

/**
 * A long list can be divided into several pages using Pagination, and only one page will be loaded at a time.
 */
var Pagination = function Pagination(_ref) {
  var count = _ref.count,
    current = _ref.current,
    onChange = _ref.onChange,
    _ref$clickableCurrent = _ref.clickableCurrent,
    clickableCurrent = _ref$clickableCurrent === void 0 ? false : _ref$clickableCurrent,
    nextLabel = _ref.nextLabel,
    nextLabelDataElementName = _ref.nextLabelDataElementName,
    previousLabel = _ref.previousLabel,
    previousLabelDataElementName = _ref.previousLabelDataElementName,
    fastForwardLabel = _ref.fastForwardLabel,
    fastBackwardLabel = _ref.fastBackwardLabel,
    navigationSectionLabel = _ref.navigationSectionLabel,
    itemLabel = _ref.itemLabel,
    activeItemLabel = _ref.activeItemLabel,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _React$useState = React.useState(current),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    currentIndex = _React$useState2[0],
    setCurrentIndex = _React$useState2[1];
  React.useEffect(function () {
    setCurrentIndex(current);
  }, [current]);
  var pageOptions = React.useMemo(function () {
    return getPageOptions(currentIndex, count);
  }, [count, currentIndex]);
  var handlePageChange = React.useCallback(function (pageIndex) {
    onChange(Math.min(Math.max(pageIndex, 0), count - 1));
  }, [onChange, count]);
  var handlePageClick = React.useCallback(function (pageIndex, e) {
    e.preventDefault();
    handlePageChange(pageIndex);
  }, [handlePageChange]);
  var handleFastForward = React.useCallback(function (pageIndex) {
    if (pageIndex === PREVIOUS_FORWARD_VALUE) {
      handlePageChange(currentIndex - MAX_TO_SHOW >= 0 ? currentIndex - MAX_TO_SHOW : 0);
    } else if (pageIndex === NEXT_FORWARD_VALUE) {
      handlePageChange(currentIndex + MAX_TO_SHOW < count - 1 ? currentIndex + MAX_TO_SHOW : count - 1);
    }
  }, [handlePageChange, currentIndex, count]);
  var goToPreviousPage = React.useCallback(function () {
    return handlePageChange(currentIndex - 1);
  }, [currentIndex, handlePageChange]);
  var goToNextPage = React.useCallback(function () {
    return handlePageChange(currentIndex + 1);
  }, [currentIndex, handlePageChange]);
  var renderPageIndicator = React.useCallback(function (pageIndex) {
    var ariaLabelActive = activeItemLabel ? "".concat(activeItemLabel, " ").concat(pageIndex + 1) : pageIndex + 1;
    if (pageIndex === currentIndex && !clickableCurrent) {
      return /*#__PURE__*/React.createElement(Box, {
        as: "button",
        disabled: true,
        "aria-current": "true",
        "aria-label": ariaLabelActive,
        m: "0",
        p: "0"
      }, /*#__PURE__*/React.createElement(Typography, {
        variant: "body-default-strong",
        textColor: "product.primary"
      }, pageIndex + 1));
    }
    if (pageIndex < 0) {
      return /*#__PURE__*/React.createElement(FastForwardIndicator, {
        direction: pageIndex === PREVIOUS_FORWARD_VALUE ? 'left' : 'right',
        onClick: function onClick() {
          return handleFastForward(pageIndex);
        },
        fastForwardLabel: fastForwardLabel,
        fastBackwardLabel: fastBackwardLabel
      });
    }
    var ariaLabel = itemLabel ? "".concat(itemLabel, " ").concat(pageIndex + 1) : pageIndex + 1;
    if (pageIndex === currentIndex && clickableCurrent) {
      return /*#__PURE__*/React.createElement(Box, {
        as: "button",
        "aria-label": ariaLabelActive,
        "aria-current": "true",
        m: "0",
        p: "0",
        onClick: function onClick(e) {
          return handlePageClick(pageIndex, e);
        }
      }, /*#__PURE__*/React.createElement(TypographyStyled, {
        textColor: "product.primary",
        variant: "body-default-strong"
      }, pageIndex + 1));
    }
    return /*#__PURE__*/React.createElement(Box, {
      as: "button",
      "aria-label": ariaLabel,
      "aria-current": "false",
      m: "0",
      p: "0",
      onClick: function onClick(e) {
        return handlePageClick(pageIndex, e);
      }
    }, /*#__PURE__*/React.createElement(TypographyStyled, {
      textColor: "base.subtle",
      variant: "body-default-strong"
    }, pageIndex + 1));
  }, [currentIndex, clickableCurrent, itemLabel, activeItemLabel, fastForwardLabel, fastBackwardLabel, handleFastForward, handlePageClick]);
  return /*#__PURE__*/React.createElement(Box, _extends({
    as: "nav",
    "aria-label": navigationSectionLabel,
    display: "flex",
    alignItems: "center"
  }, rest), /*#__PURE__*/React.createElement(ButtonMemoized, _extends({
    variant: "icon",
    hierarchy: "tertiary",
    size: "s",
    leadingIcon: "fill.arrow.thin-back",
    state: currentIndex > 0 ? 'enabled' : 'disabled',
    onClick: goToPreviousPage,
    label: previousLabel
  }, previousLabelDataElementName && {
    'data-element-name': previousLabelDataElementName
  })), /*#__PURE__*/React.createElement(Box, {
    as: "ul",
    display: "flex",
    alignItems: "center",
    listStyle: "none",
    m: "0",
    p: "0"
  }, pageOptions.map(function (pageIndex) {
    return /*#__PURE__*/React.createElement(Box, {
      as: "li",
      "aria-setsize": count,
      "aria-posinset": pageIndex < 0 ? undefined : pageIndex + 1,
      listStyle: "none",
      key: pageIndex,
      borderStyle: pageIndex === currentIndex ? 'solid' : 'none',
      borderRadius: "8",
      m: "4",
      minWidth: "28px",
      minHeight: "28px",
      borderColor: "product.primary.strong",
      borderWidth: "2",
      display: "flex",
      justifyContent: "center",
      p: "2"
    }, renderPageIndicator(pageIndex));
  })), /*#__PURE__*/React.createElement(ButtonMemoized, _extends({
    variant: "icon",
    hierarchy: "tertiary",
    size: "s",
    leadingIcon: "fill.arrow.thin-forward",
    state: currentIndex < count - 1 ? 'enabled' : 'disabled',
    onClick: goToNextPage,
    label: nextLabel
  }, nextLabelDataElementName && {
    'data-element-name': nextLabelDataElementName
  })));
};
Pagination.defaultProps = {
  clickableCurrent: false
};
var PaginationMemoized = /*#__PURE__*/React.memo(Pagination);
PaginationMemoized.displayName = 'Pagination';

export { Pagination as BasePagination, PaginationMemoized as Pagination };
//# sourceMappingURL=Pagination.js.map
